import { Tag, Tooltip, Typography } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import type { Controller } from "../../../services/licensing/client/types.gen";
import type { SiteIoConfiguration } from "../../../services/client/types.gen";
import type { ControllerStatus } from "./controllerStatus";

const { Text } = Typography;

type ControllerCardProps = {
  controller: Controller;
  status: ControllerStatus;
  partnerName?: string;
  siteIoConfiguration?: SiteIoConfiguration;
  onOpenSettings?: () => void;
};

const SiteIoConfigurationSummary: React.FC<{
  configuration?: SiteIoConfiguration;
}> = ({ configuration }) => {
  if (!configuration) {
    return (
      <Text type="secondary" className="text-xs">
        no IO configuration
      </Text>
    );
  }

  const controllerMode = configuration.controller_mode ?? "active";
  const phasePermutationMode =
    configuration.phase_permutation_mode ?? "worst_case";

  return (
    <span className="flex flex-wrap items-center gap-1">
      <Tag
        className="m-0"
        color={controllerMode === "active" ? "green" : "orange"}
      >
        {controllerMode === "active" ? "active" : "read only"}
      </Tag>
      <Tag
        className="m-0"
        color={phasePermutationMode === "use_topology" ? "green" : "default"}
      >
        {phasePermutationMode}
      </Tag>
    </span>
  );
};

const VersionSummary: React.FC<{
  controller: Controller;
  status: ControllerStatus;
}> = ({ controller, status }) => {
  const { current_version, target_version } = controller;

  switch (status) {
    case "unclaimed":
      return (
        <Tag color="blue" className="m-0">
          unclaimed
        </Tag>
      );

    case "mismatch":
      return (
        <span className="flex items-center gap-1">
          <Tag color="orange" className="m-0">
            running {current_version}
          </Tag>
          <ArrowRightOutlined className="text-xs text-gray-400" />
          <Tag className="m-0">target {target_version}</Tag>
        </span>
      );

    case "up_to_date":
      return (
        <Tag color="green" className="m-0" icon={<CheckOutlined />}>
          {current_version}
        </Tag>
      );

    case "unknown":
      return (
        <span className="flex items-center gap-1">
          <Tag className="m-0">{current_version ?? "never reported"}</Tag>
          {!target_version && (
            <Tooltip title="No target version set on this controller, so nothing can be compared. Ansible refuses to deploy without one.">
              <Text type="secondary" className="text-xs">
                no target
              </Text>
            </Tooltip>
          )}
        </span>
      );
  }
};

export const ControllerCard: React.FC<ControllerCardProps> = ({
  controller,
  status,
  partnerName,
  siteIoConfiguration,
  onOpenSettings,
}) => {
  const hostname = controller.device_info?.serial_numbers?.hostname;

  return (
    <div
      className={`rounded border border-gray-200 bg-white p-3 shadow-sm ${
        onOpenSettings ? "hover:cursor-pointer hover:shadow-md" : ""
      }`}
      onClick={onOpenSettings}
    >
      <div className="flex items-start justify-between gap-2">
        <Text strong className="break-all leading-tight">
          {hostname ?? controller.device_info?.model_name ?? "Controller"}
        </Text>
        {onOpenSettings && (
          <Tooltip title="Open controller settings of this site">
            <SettingOutlined className="text-gray-400" />
          </Tooltip>
        )}
      </div>

      <div className="mt-2">
        <VersionSummary controller={controller} status={status} />
      </div>

      {controller.site_id && (
        <div className="mt-2">
          <SiteIoConfigurationSummary configuration={siteIoConfiguration} />
        </div>
      )}

      <div className="mt-2 flex flex-col gap-0.5">
        {controller.site_id && (
          <Text type="secondary" className="text-xs break-all">
            site {controller.site_id}
          </Text>
        )}
        {partnerName && (
          <Text type="secondary" className="text-xs break-all">
            {partnerName}
          </Text>
        )}
        {controller._id && (
          <Text
            type="secondary"
            className="text-xs break-all"
            copyable={{ text: controller._id }}
            onClick={(event) => event.stopPropagation()}
          >
            {controller._id}
          </Text>
        )}
      </div>
    </div>
  );
};
